import { useEffect, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { KPI } from "@/components/Primitives";
import { fmtTime } from "@/lib/time";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, Star, Ticket } from "lucide-react";
import { toast } from "sonner";

// A card per pending CSAT survey. POSTs to /api/csat/{token} -- the SAME
// endpoint an emailed link would hit, so there is one submission path, not
// two. The token comes from GET /api/portal/csat, which only ever hands
// back surveys issued to THIS user (see authz.py / csat.py).
function SurveyCard({ survey, onDone }) {
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!score) { toast.error("Pick a rating from 1 to 5"); return; }
    setBusy(true);
    try {
      await api.post(`/csat/${survey.token}`, { score, comment: comment.trim() || undefined });
      setDone(true);
      toast.success("Thanks for the feedback");
      setTimeout(() => onDone(survey.survey_id), 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit -- the link may have expired");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 text-sm text-emerald-400"
           data-testid={`csat-thankyou-${survey.survey_id}`}>
        Thanks -- your feedback on ticket #{survey.ticket_number ?? "—"} was recorded.
      </div>
    );
  }

  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3"
         data-testid={`csat-card-${survey.survey_id}`}>
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
          Ticket #{survey.ticket_number ?? "—"}
        </div>
        <div className="font-display font-medium">{survey.ticket_title ?? "Untitled ticket"}</div>
        <div className="text-xs text-zinc-500 mt-0.5">Issued {fmtTime(survey.issued_at)}</div>
      </div>
      <div className="flex items-center gap-1" data-testid={`csat-stars-${survey.survey_id}`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" onClick={() => setScore(n)}
                  data-testid={`csat-score-${survey.survey_id}-${n}`}
                  aria-label={`Rate ${n} out of 5`}
                  className={`p-1 rounded ${n <= score ? "text-amber-400" : "text-zinc-700 hover:text-zinc-500"}`}>
            <Star size={20} fill={n <= score ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Anything you'd like to add (optional)"
                data-testid={`csat-comment-${survey.survey_id}`}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-md p-2 text-sm text-zinc-200 placeholder-zinc-600"
                rows={2} />
      <button onClick={submit} disabled={busy} data-testid={`csat-submit-${survey.survey_id}`}
              className="text-sm bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-md px-3 py-1.5">
        {busy ? "Submitting…" : "Submit"}
      </button>
    </div>
  );
}

// Mirrors backend/authz.py's CLIENT_SCOPED_ROLES (copied, for the same
// reason, in Layout.jsx / App.js / PortalTickets.jsx / NewTicketDialog.jsx).
// COPY ONLY: it decides whether this page shows the client PICKER; the
// server decides what the API answers.
const CLIENT_SCOPED_ROLES = [
  "client_read_only", "client_admin", "client_administrator",
  "client_manager", "client_user", "client_contact",
];

export default function ClientPortal() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  // Staff reach this page from the console's "Client Portal" entry. The
  // overview is per client, and a staff account has no client of its own,
  // so staff pick one and the server is asked for THAT client
  // (`GET /portal/overview?client_id=`). Before 2026-10-16 the server
  // refused every staff caller and the refusal was swallowed, so this page
  // showed "Loading portal…" for ever -- the "we cannot access the client
  // portal" a buyer reported.
  const staffViewer = !!user && !CLIENT_SCOPED_ROLES.includes(user.role);
  const picked = params.get("client_id") || "";
  const [clients, setClients] = useState([]);
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [surveys, setSurveys] = useState([]);

  useEffect(() => {
    if (!staffViewer) return;
    api.get("/clients").then((r) => {
      const rows = asArray(r.data?.items ?? r.data);
      setClients(rows);
      // Default to the first client so the page never waits on an unmade choice.
      if (!picked && rows.length) setParams({ client_id: rows[0].id }, { replace: true });
    }).catch((e) => setErr(formatApiError(e)));
  }, [staffViewer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Wait for the session: before `user` resolves, `staffViewer` is false
    // and a staff account would fire one client-less request, draw its 403
    // for a frame, then fetch again. The first request should be the right one.
    if (!user) return;
    if (staffViewer && !picked) return;
    setO(null);
    setErr("");
    api.get("/portal/overview", { params: staffViewer ? { client_id: picked } : {} })
      .then((r) => setO(r.data))
      // Say what the server said. A swallowed refusal reads as a hung page.
      .catch((e) => setErr(formatApiError(e)));
    // /portal/csat is the customer's own surface (403 to staff), and a
    // survey list is a bonus on this page, not its reason to exist -- a
    // failure here must not block the overview.
    if (staffViewer) return;
    api.get("/portal/csat").then(r => setSurveys(r.data.items || [])).catch(() => {});
  }, [user, staffViewer, picked]);
  const dismiss = (id) => setSurveys((prev) => prev.filter((s) => s.survey_id !== id));

  const picker = staffViewer && (
    <div className="flex items-center gap-3 flex-wrap" data-testid="portal-staff-preview">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Preview as client</span>
      <select value={picked} onChange={(e) => setParams({ client_id: e.target.value })}
              data-testid="portal-client-picker"
              className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500/60">
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <span className="text-xs text-zinc-500">What this client's contacts see when they sign in.</span>
    </div>
  );
  if (err) return (
    <div className="space-y-6">
      {picker}
      <div className="text-sm text-red-400" data-testid="portal-error">{err}</div>
    </div>
  );
  if (!o || !o.client) return (
    <div className="space-y-6">
      {picker}
      <div className="text-zinc-500 text-sm">Loading portal…</div>
    </div>
  );
  return (
    <div className="space-y-6">
      {picker}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
          {staffViewer ? "Client Portal · staff preview" : "Client Portal"}
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2"><Building2 size={22} className="text-blue-400"/> {o.client.name}</h1>
        <p className="text-sm text-zinc-500 mt-1">Read-only view exposed to client administrators · {o.client.industry} · {o.client.country}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI label="Assets" value={o.assets} testid="portal-assets"/>
        <KPI label="Open Tickets" value={o.open_tickets} testid="portal-tickets"/>
        {/* Not the same number as "Open Tickets": this one is server-computed
            (psa.annotate_overdue), live, and excludes a ticket currently
            paused or awaiting an approval decision -- its clock has stopped,
            so it is not overdue, whatever its stored deadline says. */}
        <KPI label="Past due SLA" value={o.sla_overdue ?? 0}
             tone={o.sla_overdue ? "bad" : "default"} testid="portal-sla-overdue"/>
        <KPI label="Compliance" value={o.compliance_findings} tone="warn" testid="portal-comp"/>
        <KPI label="Security" value={o.security_findings} tone="warn" testid="portal-sec"/>
      </div>
      {/* Sub-project I, Task 10. The "Open Tickets" figure above had nothing
          behind it: a customer could read the count and then had no way to
          see what it counted. This is that door. */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-display font-medium">Your tickets</div>
          <p className="text-sm text-zinc-400 mt-0.5">
            See everything your organisation has raised, add to a ticket, or raise a new one.
          </p>
        </div>
        <Link to={staffViewer ? `/tickets?client_id=${encodeURIComponent(picked)}` : "/portal/tickets"}
              data-testid="portal-tickets-link"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-md px-3 py-1.5 text-sm shrink-0 inline-flex items-center gap-1.5">
          <Ticket size={15} /> Open ticket list
        </Link>
      </div>
      {surveys.length > 0 && (
        <div className="space-y-3" data-testid="csat-survey-list">
          <div className="font-display font-medium">How did we do?</div>
          {surveys.map((s) => <SurveyCard key={s.survey_id} survey={s} onDone={dismiss} />)}
        </div>
      )}
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
        <div className="font-display font-medium mb-2">Available in client portal</div>
        <ul className="text-sm text-zinc-400 list-disc list-inside space-y-1">
          <li>View Assets, Tickets, Compliance Status, Security Status</li>
          <li>Download Reports (PDF / Excel / PowerPoint)</li>
          <li>View Contracts and Licenses</li>
          <li>Executive Dashboards (read-only)</li>
        </ul>
      </div>
    </div>
  );
}
